import React from 'react';
import { Sparkles, MapPin, CheckCircle2, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { REQUEST_STATUS_LABELS, type RequestPin, type RequestStatus, type SlaState } from '../../utils/requestPins';
const STATUS_STYLES: Record<RequestStatus, string> = {
  nouvelle: 'bg-indigo-50 text-[#4F46E5]',
  assignee: 'bg-sky-50 text-sky-700',
  en_cours: 'bg-slate-100 text-slate-700',
  en_attente: 'bg-amber-50 text-amber-700'
};
export const SLA_COLORS: Record<SlaState, string> = {
  ok: '#059669',
  risk: '#D97706',
  late: '#DC2626'
};

/**
 * Deadline bar following bullet-chart conventions:
 * - solid fill = time elapsed within the SLA;
 * - hatched red = overrun beyond the SLA (late);
 * - dashed outline = remaining budget until the SLA (on time / at risk);
 * - dark tick = the SLA target, with values labelled above the bar.
 */
function SlaTimeline({
  pin,
  color,
  stateLabel
}: {
  pin: RequestPin;
  color: string;
  stateLabel: string;
}) {
  const {
    t
  } = useLanguage();
  const late = pin.days > pin.slaDays;
  const scale = Math.max(pin.days, pin.slaDays);
  const currentPct = pin.days / scale * 100;
  const slaPct = pin.slaDays / scale * 100;
  const gap = Math.abs(pin.days - pin.slaDays);
  const d = (n: number) => n > 1 ? t(`${n} jours`, `${n} days`) : t(`${n} jour`, `${n} day`);
  const title = late ? t(`${stateLabel} de ${d(gap)}`, `${stateLabel} by ${d(gap)}`) : t(`${stateLabel} · ${d(gap)} restants`, `${stateLabel} · ${d(gap)} left`);
  const dd = t('j', 'd');
  return <div className="flex flex-col gap-1.5" data-id="element-5518" data-mp="27s1n1195s2yo">
      <span className="text-xs font-semibold" style={{
      color
    }} data-id="element-5519" data-mp="2mj2b2eme25fx">
        {title}
      </span>

      {/* Value labels above the bar */}
      <div className="relative h-4 text-[11px] leading-4 tabular-nums" aria-hidden="true" data-id="element-5520" data-mp="2hqr8t546e7k7">
        {late ? <>
            <span className="absolute top-0 -translate-x-full pr-1 text-slate-500 whitespace-nowrap" style={{
          left: `clamp(56px, ${slaPct}%, calc(100% - 36px))`
        }} data-id="element-5521" data-mp="1p3ej4m2pxjmo">
              {t('SLA', 'SLA')} {pin.slaDays} {dd}
            </span>
            <span className="absolute top-0 right-0 font-bold whitespace-nowrap" style={{
          color
        }} data-id="element-5522" data-mp="3u2mnmtd8bv4c">
              {pin.days} {dd}
            </span>
          </> : <>
            <span className="absolute top-0 -translate-x-full pr-1 font-bold whitespace-nowrap" style={{
          left: `clamp(32px, ${currentPct}%, calc(100% - 64px))`,
          color
        }} data-id="element-5523" data-mp="6qvev7c3hzgc">
              {pin.days} {dd}
            </span>
            <span className="absolute top-0 right-0 text-slate-500 whitespace-nowrap" data-id="element-5524" data-mp="3f7707775mxgx">
              {t('SLA', 'SLA')} {pin.slaDays} {dd}
            </span>
          </>}
      </div>

      <div role="img" aria-label={t(`${pin.days} jours écoulés sur un SLA de ${pin.slaDays} jours`, `${pin.days} days elapsed against a ${pin.slaDays}-day SLA`)} className="relative h-2.5" data-id="element-5525" data-mp="uhr5400wkmlh">
        {/* Elapsed within the SLA */}
        <div className="absolute inset-y-0 left-0 rounded-l-full" style={{
        width: `${late ? slaPct : currentPct}%`,
        background: color,
        borderTopRightRadius: late ? 0 : undefined
      }} data-id="element-5526" data-mp="14lfufwzn6w5v" />
        {late ? (/* Overrun: hatched */
      <div className="absolute inset-y-0 rounded-r-full" style={{
        left: `${slaPct}%`,
        right: 0,
        backgroundColor: `${color}1F`,
        backgroundImage: `repeating-linear-gradient(-45deg, ${color} 0 2px, transparent 2px 5px)`
      }} data-id="element-5527" data-mp="qu1e5xx4d92i" />) : (/* Remaining budget: dashed outline */
      <div className="absolute inset-y-0 rounded-r-full" style={{
        left: `${currentPct}%`,
        right: 0,
        border: `1.5px dashed ${color}99`,
        borderLeft: 'none'
      }} data-id="element-5528" data-mp="2ba4p5weq21ai" />)}
        {/* SLA tick */}
        <div className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-[#0F172A]" style={{
        left: `calc(${slaPct}% - 1px)`
      }} data-id="element-5529" data-mp="2jau5tvzu0b1n" />
      </div>

    </div>;
}
interface RequestDetailPanelProps {
  pin: RequestPin;
  onClose: () => void;
}

/** Request detail shown in the right-hand analytics panel. */
export function RequestDetailPanel({
  pin,
  onClose
}: RequestDetailPanelProps) {
  const {
    t
  } = useLanguage();
  return <div className="border border-slate-200 rounded-xl shadow-sm bg-white flex flex-col h-full overflow-hidden font-body" data-id="element-5530" data-mp="20crbd1oillwg">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2" data-id="element-5531" data-mp="1lkphby94ln4c">
        <button type="button" onClick={onClose} aria-label={t('Fermer le détail', 'Close detail')} className="w-7 h-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-[#0F172A] hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]" data-id="element-5532" data-mp="1qjka9t0t4s26">
          <X className="w-4 h-4" data-id="element-5533" data-mp="t13ui4xysalt" />
        </button>
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-5534" data-mp="t2eurus7b086">
          {t('Détail de la requête', 'Request detail')}
        </span>
      </div>
      <div className="p-5 overflow-y-auto" data-id="element-5535" data-mp="12wotdu7qkurc">
        <RequestPinPopup pin={pin} data-id="element-5536" data-mp="1gxrgmaagwb3u" />
      </div>
    </div>;
}
export function RequestPinPopup({
  pin
}: {
  pin: RequestPin;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const color = SLA_COLORS[pin.state];
  const stateLabel = pin.state === 'late' ? t('SLA dépassé', 'SLA missed') : pin.state === 'risk' ? t('Proche de l’échéance', 'Near deadline') : t('SLA respecté', 'SLA met');
  const explanationTitle = pin.state === 'late' ? t('Pourquoi le SLA n’est pas respecté', 'Why the SLA is missed') : pin.state === 'risk' ? t('Risque de dépassement', 'Risk of missing the SLA') : t('Pourquoi c’est dans les temps', 'Why it’s on time');
  return <div className="w-full flex flex-col gap-4 font-body text-[#0F172A]" data-id="element-5537" data-mp="xaupb6l0ry2q">
      <div className="flex flex-col gap-0.5" data-id="element-5538" data-mp="x8qcu0921qqu">
        <div className="flex items-end justify-between gap-2" data-id="element-5539" data-mp="11bn1o8p6uj38">
          <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide" data-id="element-5540" data-mp="163ej9tqd9eh8">
            {pin.category[lang]}
          </span>
          <div className="flex flex-col items-end gap-1 shrink-0" data-id="element-5541" data-mp="1b1psq3cr1myx">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[pin.status]}`} data-id="element-5542" data-mp="1g1c9gmrsdlch">
              <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" data-id="element-5543" data-mp="1kyuied5t00kh" />
              {REQUEST_STATUS_LABELS[pin.status][lang]}
            </span>
            <span className="text-[11px] text-slate-400" data-id="element-5544" data-mp="3mtd8i6da4q6z">#{pin.number}</span>
          </div>
        </div>
        <div className="text-sm font-bold font-heading leading-snug" data-id="element-5545" data-mp="3qu5k369mj76f">
          {pin.subcategory[lang]}
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500" data-id="element-5546" data-mp="3jc7e3bqnie7y">
          <MapPin className="w-3 h-3" data-id="element-5547" data-mp="3eimfbhh8hg7p" /> {pin.address}
        </div>
      </div>

      <SlaTimeline pin={pin} color={color} stateLabel={stateLabel} data-id="element-5548" data-mp="39jjl6s2skuei" />

      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 flex flex-col gap-2.5" data-id="element-5549" data-mp="35ipxnzt9hhm5">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#4F46E5]" data-id="element-5550" data-mp="63cfkg0tgn33">
          <Sparkles className="w-3.5 h-3.5" data-id="element-5551" data-mp="14hxxhel54kk" /> ATNA
        </div>
        <p className="text-xs text-slate-700 leading-relaxed m-0" data-id="element-5552" data-mp="1514y32p68jt">{pin.summary[lang]}</p>
        <div className="flex flex-col gap-1" data-id="element-5553" data-mp="3t6z9qa0oar03">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide" data-id="element-5554" data-mp="3o97jrljl9eyj">
            {t('Actions', 'Actions')}
          </div>
          <ul className="flex flex-col gap-1 m-0 p-0 list-none" data-id="element-5555" data-mp="2htwvhlm1qdj3">
            {pin.actions.map(a => <li key={a.fr} className="flex items-start gap-1.5 text-xs text-slate-700" data-id="element-5556" data-mp="2ct92wvi65omr">
                <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0 text-slate-400" data-id="element-5557" data-mp="gq4frs23duij" />
                {a[lang]}
              </li>)}
          </ul>
        </div>
        <div className="flex flex-col gap-1 pt-2 border-t border-indigo-100" data-id="element-5558" data-mp="6t9hna9qdw4s">
          <div className="text-[11px] font-medium uppercase tracking-wide" style={{
          color
        }} data-id="element-5559" data-mp="brt6pjjmfo1d">
            {explanationTitle}
          </div>
          <p className="text-xs text-slate-700 leading-relaxed m-0" data-id="element-5560" data-mp="zighcckx8rrm">{pin.explanation[lang]}</p>
        </div>
      </div>
    </div>;
}